import React, { useEffect, useRef } from 'react';

export function GamerBackground() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        let mouse = { x: null, y: null };

        const resize = () => {
            if (canvas.parentElement) {
                canvas.width = canvas.parentElement.clientWidth;
                canvas.height = canvas.parentElement.clientHeight;
            } else {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            }
            initGrid();
        };

        // Grid properties
        const spacing = 50;
        let gridPoints = [];

        const initGrid = () => {
            gridPoints = [];
            for (let x = 0; x <= canvas.width + spacing; x += spacing) {
                for (let y = 0; y <= canvas.height + spacing; y += spacing) {
                    gridPoints.push({ x, y, baseSize: 1.5 });
                }
            }
        };

        // Cyber Particles
        const particleCount = 70;
        const particles = [];
        class CyberParticle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.vx = (Math.random() - 0.5) * 1.5;
                this.vy = (Math.random() - 0.5) * 1.5;
                this.size = Math.random() * 2 + 1;
                this.color = Math.random() > 0.5 ? '#ec4899' : '#a855f7'; // Pink or Purple
            }
            update() {
                if (this.x < 0 || this.x > canvas.width) this.vx = -this.vx;
                if (this.y < 0 || this.y > canvas.height) this.vy = -this.vy;
                this.x += this.vx;
                this.y += this.vy;
            }
            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = this.color;
                ctx.shadowBlur = 15;
                ctx.shadowColor = this.color;
                ctx.fill();
                ctx.shadowBlur = 0;
            }
        }

        for (let i = 0; i < particleCount; i++) {
            particles.push(new CyberParticle());
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw interactive crosshair grid
            for (const pt of gridPoints) {
                let size = pt.baseSize;
                let alpha = 0.15;
                let isGlowing = false;
                
                if (mouse.x !== null && mouse.y !== null) {
                    const dx = mouse.x - pt.x;
                    const dy = mouse.y - pt.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const maxDist = 200;
                    if (dist < maxDist) {
                        const intensity = 1 - (dist / maxDist);
                        size += intensity * 2;
                        alpha = 0.15 + intensity * 0.35;
                        isGlowing = true;
                    }
                }
                
                const isDark = document.documentElement.classList.contains('dark');
                const baseColor = isDark ? `rgba(255, 255, 255, ${alpha})` : `rgba(0, 0, 0, ${alpha})`;
                ctx.fillStyle = isGlowing ? `rgba(236, 72, 153, ${alpha})` : baseColor;
                
                // Draw a small plus sign (+) for high-tech grid feel
                ctx.fillRect(pt.x - size, pt.y - size/3, size*2, size/1.5);
                ctx.fillRect(pt.x - size/3, pt.y - size, size/1.5, size*2);
            }

            // Draw particles and connect them to mouse
            for (const p of particles) {
                p.update();
                p.draw();

                if (mouse.x !== null && mouse.y !== null) {
                    const dx = mouse.x - p.x;
                    const dy = mouse.y - p.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 220) {
                        ctx.beginPath();
                        // Gradient laser line
                        const grad = ctx.createLinearGradient(p.x, p.y, mouse.x, mouse.y);
                        grad.addColorStop(0, p.color);
                        grad.addColorStop(1, 'transparent');
                        
                        ctx.strokeStyle = grad;
                        ctx.lineWidth = 1.5 * (1 - dist / 220);
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(mouse.x, mouse.y);
                        ctx.stroke();
                    }
                }
            }

            // Optional: Draw a target reticle around the mouse
            if (mouse.x !== null && mouse.y !== null) {
                ctx.beginPath();
                ctx.strokeStyle = 'rgba(236, 72, 153, 0.15)'; // Pink reticle
                ctx.lineWidth = 1;
                ctx.arc(mouse.x, mouse.y, 40, 0, Math.PI * 2);
                ctx.stroke();

                // Crosshairs
                ctx.beginPath();
                ctx.moveTo(mouse.x - 50, mouse.y);
                ctx.lineTo(mouse.x - 30, mouse.y);
                ctx.moveTo(mouse.x + 30, mouse.y);
                ctx.lineTo(mouse.x + 50, mouse.y);
                ctx.moveTo(mouse.x, mouse.y - 50);
                ctx.lineTo(mouse.x, mouse.y - 30);
                ctx.moveTo(mouse.x, mouse.y + 30);
                ctx.lineTo(mouse.x, mouse.y + 50);
                ctx.stroke();
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        const handleMouseMove = (e) => {
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            mouse.x = e.clientX - rect.left;
            mouse.y = e.clientY - rect.top;
        };

        const handleMouseLeave = () => {
            mouse.x = null;
            mouse.y = null;
        };

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', handleMouseMove);
        document.body.addEventListener('mouseleave', handleMouseLeave);
        
        resize();
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            document.body.removeEventListener('mouseleave', handleMouseLeave);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none z-0 opacity-40 md:opacity-70 transition-opacity duration-300"
            style={{ width: '100%', height: '100%' }}
        />
    );
}
